import { restartTutorial } from '../../tutorial/events'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight, Settings } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field } from '../components/Form'
import { EmptyState, ErrorState, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useChannels, useClubOverview, useFeedbackThreads, useTeam } from '../data/hooks'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import { useOnline } from '../platform/network'
import { useBackTo } from './AthleteScreen'
import { LeaveClubSheet, ThemePicker } from './ClubSettingsScreens'

const ROLE_LABEL = { owner: 'Владелец клуба', admin: 'Тренер с полным доступом' } as const

function Row({ title, meta, onClick }: { title: string; meta?: string; onClick?: () => void }) {
  const inner = (
    <>
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {title}
        </span>
        {meta && <span className="pc-list-row__meta">{meta}</span>}
      </span>
      {onClick && <ChevronRight size={18} aria-hidden="true" className="pc-muted" />}
    </>
  )
  return onClick ? (
    <button type="button" className="pc-list-row" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className="pc-list-row" style={{ cursor: 'default' }}>
      {inner}
    </div>
  )
}

/**
 * «Обратная связь» с бейджем «Новый ответ», если поддержка ответила, а
 * переписку ещё не открывали. Список не загрузился - строка без бейджа.
 */
function FeedbackRow({ onClick }: { onClick: () => void }) {
  const threads = useFeedbackThreads()
  const unread = threads.data?.reduce((n, t) => n + t.unread, 0) ?? 0
  return (
    <button type="button" className="pc-list-row" onClick={onClick} aria-label={unread > 0 ? 'Обратная связь, новый ответ' : undefined}>
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          Обратная связь
        </span>
        <span className="pc-list-row__meta">Ошибка, вопрос или идея команде ProCoach</span>
      </span>
      {unread > 0 && (
        <span className="pc-badge" aria-hidden="true">
          Новый ответ
        </span>
      )}
      <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
    </button>
  )
}

function dateLabel(iso: string | undefined): string | null {
  const d = parseIsoDate(iso?.slice(0, 10))
  return d ? `${formatDayShort(d)} ${d.getFullYear()}` : null
}

// ---------------------------------------------------------------------------
// S11 Клуб и аккаунт
// ---------------------------------------------------------------------------

/**
 * S11: клубные настройки (влияют на команду) отдельно от личных (касаются
 * только меня). Роль видна сразу.
 */
export function ClubScreen({
  onLegacy,
  onSignOut,
}: {
  onLegacy?: () => void
  /** Выход из аккаунта: только PWA. Без него кнопки «Выйти» нет. */
  onSignOut?: () => void | Promise<void>
}) {
  const navigate = useNavigate()
  const overview = useClubOverview()
  const [leaving, setLeaving] = useState(false)

  let body
  if (overview.isPending) {
    body = <Skeleton rows={4} label="Загружаю клуб" />
  } else if (overview.isError) {
    body = <ErrorState title="Не удалось загрузить клуб" onRetry={() => void overview.refetch()} />
  } else {
    const { club, clubs, directions } = overview.data
    const current = directions.find((d) => d.current)
    body = (
      <>
        <button type="button" className="pc-btn pc-btn--block" onClick={restartTutorial}>Пройти обучение заново</button>
        <span className="pc-badge" style={{ marginTop: 12 }}>
          {ROLE_LABEL[club.role]}
        </span>

        <section className="pc-section" aria-labelledby="pc-club-title">
          <h2 id="pc-club-title" className="pc-section-title">
            {club.name}
          </h2>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Настройки клуба касаются всей команды.
          </p>
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            <li>
              <Row
                title="Направления"
                meta={current ? `Сейчас: ${current.name}` : `${directions.length}`}
                onClick={() => navigate('/club/directions')}
              />
            </li>
            {/* Материалы направления (этап 5 шаг 3, S20): вход из клуба. */}
            <li>
              <Row
                title="Материалы"
                meta={current ? `${current.name}: требования, нормативы, правила` : 'Требования, нормативы, правила'}
                onClick={() => navigate('/club/materials')}
              />
            </li>
            <li>
              <Row
                title="Тренеры направления"
                meta={current ? `Справочник «${current.name}»` : 'Справочник тренеров'}
                onClick={() => navigate('/club/coaches')}
              />
            </li>
            <li>
              <Row title="Команда и доступы" meta="Тренеры и приглашения" onClick={() => navigate('/club/team')} />
            </li>
            <li>
              <Row title="Подписка клуба" meta="Тариф и лимиты" onClick={() => navigate('/club/subscription')} />
            </li>
            {clubs.length > 1 && (
              <li>
                <Row title="Другой клуб" meta={`Ваших клубов: ${clubs.length}`} onClick={() => navigate('/club/switch')} />
              </li>
            )}
            {club.role === 'owner' && (
              <li>
                <Row title="Название клуба" meta={club.name} onClick={() => navigate('/club/rename')} />
              </li>
            )}
            <li>
              <Row title="Вступить в другой клуб" meta="По коду приглашения" onClick={() => navigate('/club/join')} />
            </li>
          </ul>
          {club.role === 'owner' ? (
            <button type="button" className="pc-btn pc-btn--block pc-btn--danger" style={{ marginTop: 8 }} onClick={() => navigate('/club/delete')}>
              Удалить клуб
            </button>
          ) : (
            <button type="button" className="pc-btn pc-btn--block pc-btn--danger" style={{ marginTop: 8 }} onClick={() => setLeaving(true)}>
              Выйти из клуба
            </button>
          )}
        </section>

        <section className="pc-section" aria-labelledby="pc-me-title">
          <h2 id="pc-me-title" className="pc-section-title">
            Мой аккаунт
          </h2>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Касается только вас.
          </p>
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            <li>
              <Row title="Связанные каналы" meta="Telegram / MAX / Web" onClick={() => navigate('/club/channels')} />
            </li>
            <li>
              <Row title="Уведомления" meta="Сейчас уведомления о подписке и команде приходят в Telegram" />
            </li>
          </ul>
          <ThemePicker />
        </section>

        <section className="pc-section" aria-labelledby="pc-app-title">
          <h2 id="pc-app-title" className="pc-section-title">
            Приложение
          </h2>
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            <li>
              <FeedbackRow onClick={() => navigate('/club/feedback')} />
            </li>
            <li>
              <Row title="О приложении" meta="Сборка и где открыто" onClick={() => navigate('/club/about')} />
            </li>
          </ul>
        </section>
        {leaving && <LeaveClubSheet overview={overview.data} onClose={() => setLeaving(false)} />}

        {onLegacy && (
          <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 24 }} onClick={onLegacy}>
            Открыть прежнюю версию
          </button>
        )}
      </>
    )
  }

  // Выйти можно и тогда, когда клуб не загрузился: без сети, при сбое сервера.
  // Кнопка всегда на одном месте дерева: начатый выход не сбрасывается, когда клуб догрузился.
  return (
    <Screen title="Клуб и аккаунт">
      {body}
      {onSignOut && <SignOut onSignOut={onSignOut} />}
    </Screen>
  )
}

/** Сколько ждать очередь Штаба на устройстве: зависшая IndexedDB не держит выход. */
const QUEUE_WAIT_MS = 2000

function within<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}

/** «1 неотправленный вопрос в Штаб будет удалён», «3 неотправленных вопроса ... будут удалены». */
export function unsentWarning(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} неотправленный вопрос в Штаб будет удалён.`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} неотправленных вопроса в Штаб будут удалены.`
  return `${n} неотправленных вопросов в Штаб будут удалены.`
}

/**
 * Выход из аккаунта (PWA, этап 4 шаг 3). Только после подтверждения. Вопросы
 * Штаба, ещё не ушедшие с устройства, при выходе удаляются - и об этом
 * говорится до выхода, с числом. Данные на устройстве, токены и переход на
 * экран входа - общий путь выхода приложения (onSignOut).
 */
function SignOut({ onSignOut }: { onSignOut: () => void | Promise<void> }) {
  const { hubQueue } = useEnv()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [checking, setChecking] = useState(false)
  // 'unknown' - очередь на устройстве не ответила
  const [unsent, setUnsent] = useState<number | 'unknown'>(0)
  const [busy, setBusy] = useState(false)

  const count = useCallback(
    () => within(hubQueue.unsentCount(), QUEUE_WAIT_MS).catch(() => 'unknown' as const),
    [hubQueue],
  )

  // Шторка открывается, когда число уже известно: нельзя выйти, не увидев предупреждения
  const ask = async () => {
    if (checking) return
    setChecking(true)
    setUnsent(await count())
    setChecking(false)
    setOpen(true)
  }

  // Пока шторка открыта, число живое: вопрос мог уйти или добавиться в другом окне
  useEffect(() => {
    if (!open) return
    let alive = true
    const off = hubQueue.subscribe((e) => {
      if (e.type !== 'changed' && e.type !== 'sent') return
      void count().then((n) => {
        if (alive && n !== 'unknown') setUnsent(n)
      })
    })
    return () => {
      alive = false
      off()
    }
  }, [open, hubQueue, count])

  const close = useCallback(() => {
    if (!busy) setOpen(false)
  }, [busy])

  const confirm = async () => {
    if (busy) return
    setBusy(true)
    try {
      await within(hubQueue.discardAll(), QUEUE_WAIT_MS)
    } catch {
      // Очередь не ответила: вопросы остаются за этим пользователем, другим на устройстве они не видны
    }
    try {
      await onSignOut()
    } catch {
      setBusy(false)
    }
  }

  const unsentN = typeof unsent === 'number' ? unsent : 0
  const warn = unsentN > 0

  return (
    <>
      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ marginTop: 24 }}
        disabled={checking}
        onClick={() => void ask()}
      >
        Выйти из аккаунта
      </button>
      {open && (
        <Sheet title="Выйти из аккаунта?" onClose={close}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            Данные клуба, сохранённые на этом устройстве для работы без сети, будут стёрты. Войти снова можно через
            Telegram, MAX или по почте.
          </p>
          {warn && (
            <div className="pc-alert" role="alert" style={{ marginBottom: 12 }}>
              {unsentWarning(unsentN)}
            </div>
          )}
          {unsent === 'unknown' && (
            <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
              Неотправленные вопросы в Штаб, если они есть на устройстве, будут удалены.
            </p>
          )}
          {!online && (
            <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
              Сейчас нет сети: войти снова получится, когда связь появится.
            </p>
          )}
          <button
            type="button"
            className={`pc-btn pc-btn--block${warn ? ' pc-btn--danger' : ' pc-btn--primary'}`}
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? 'Выхожу…' : 'Выйти'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" disabled={busy} onClick={() => setOpen(false)}>
            Отмена
          </button>
        </Sheet>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Направления и смена клуба
// ---------------------------------------------------------------------------

function useSwitch() {
  const navigate = useNavigate()
  const client = useQueryClient()
  const { toast } = useEnv()
  const [busy, setBusy] = useState<string | null>(null)
  const run = async (id: string, label: string, call: (id: string) => Promise<void>) => {
    setBusy(id)
    try {
      await call(id)
      await client.resetQueries({ queryKey: ['redesign'] })
      toast(`Переключено: ${label}`)
      navigate('/today', { replace: true })
    } catch {
      toast('Не удалось переключить. Попробуйте ещё раз.')
    } finally {
      setBusy(null)
    }
  }
  return { busy, run }
}

export function DirectionsScreen() {
  const { api } = useEnv()
  const navigate = useNavigate()
  const overview = useClubOverview()
  const back = useBackTo('/club')
  const sw = useSwitch()
  // Группа создаётся в выбранном направлении (этап 5 шаг 1).
  const current = overview.data?.directions.find((d) => d.current)

  return (
    <Screen title="Направления" back={back}>
      <p className="pc-muted" style={{ margin: '12px 0 8px', fontSize: 14 }}>
        Направление - это вид спорта внутри клуба. Расписание, группы и спортсмены показываются для выбранного.
      </p>
      {overview.isPending ? (
        <Skeleton rows={2} />
      ) : overview.isError ? (
        <ErrorState title="Не удалось загрузить направления" onRetry={() => void overview.refetch()} />
      ) : overview.data.directions.length === 0 ? (
        <EmptyState title="Направлений пока нет" />
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {overview.data.directions.map((d) => (
            <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className="pc-list-row"
                aria-current={d.current ? 'true' : undefined}
                disabled={d.current || sw.busy !== null}
                onClick={() => void sw.run(d.id, d.name, (id) => api.switchDirection(id))}
              >
                <span className="pc-list-row__main">
                  <span className="pc-list-row__title" style={{ display: 'block' }}>
                    {d.name}
                  </span>
                  <span className="pc-list-row__meta">{d.sportName || 'Вид спорта не указан'}</span>
                </span>
                {d.current ? (
                  <span className="pc-badge pc-badge--success">
                    <Check size={14} aria-hidden="true" /> Выбрано
                  </span>
                ) : sw.busy === d.id ? (
                  <span className="pc-list-row__aside">Переключаю…</span>
                ) : (
                  <span className="pc-list-row__aside">Выбрать</span>
                )}
              </button>
              {/* Название, вид спорта, удаление (этап 5 шаг 4) */}
              <button
                type="button"
                className="pc-icon-btn"
                aria-label={`Настроить «${d.name}»`}
                onClick={() => navigate(`/club/directions/${d.id}`)}
              >
                <Settings size={18} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {overview.isSuccess && (
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 12 }} onClick={() => navigate('/club/directions/new')}>
          Новое направление
        </button>
      )}
      {current && (
        <button type="button" className="pc-btn pc-btn--block" onClick={() => navigate('/groups/new')}>
          Новая группа в «{current.name}»
        </button>
      )}
    </Screen>
  )
}

export function ClubSwitchScreen() {
  const { api } = useEnv()
  const overview = useClubOverview()
  const back = useBackTo('/club')
  const sw = useSwitch()

  return (
    <Screen title="Другой клуб" back={back}>
      {overview.isPending ? (
        <Skeleton rows={2} />
      ) : overview.isError ? (
        <ErrorState title="Не удалось загрузить клубы" onRetry={() => void overview.refetch()} />
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
          {overview.data.clubs.map((c) => {
            const current = c.id === overview.data.club.id
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className="pc-list-row"
                  disabled={current || sw.busy !== null}
                  onClick={() => void sw.run(c.id, c.name, (id) => api.switchClub(id))}
                >
                  <span className="pc-list-row__main pc-list-row__title">{c.name}</span>
                  <span className="pc-list-row__aside">{current ? 'Сейчас' : sw.busy === c.id ? 'Переключаю…' : 'Перейти'}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// S12 Мои каналы
// ---------------------------------------------------------------------------

/**
 * S12: какими способами я вхожу. Привязка - одноразовым кодом: его отправляют
 * боту командой /link или вводят при регистрации в вебе. Связь не равна
 * рассылке и не копирует переписку.
 */
export function ChannelsScreen() {
  const { api } = useEnv()
  const channels = useChannels()
  const back = useBackTo('/club')
  const [code, setCode] = useState<{ code: string; minutes: number } | null>(null)
  const [codeError, setCodeError] = useState(false)
  const [busy, setBusy] = useState(false)

  const getCode = async () => {
    setBusy(true)
    setCodeError(false)
    try {
      const res = await api.createLinkCode()
      setCode({ code: res.code, minutes: res.expiresInMinutes })
    } catch {
      setCodeError(true)
    } finally {
      setBusy(false)
    }
  }

  const rows: { key: 'telegram' | 'max' | 'web'; title: string; how: string }[] = [
    { key: 'telegram', title: 'Telegram', how: 'Отправьте боту ProCoach в Telegram сообщение /link и код.' },
    { key: 'max', title: 'MAX', how: 'Отправьте боту ProCoach в MAX сообщение /link и код.' },
    { key: 'web', title: 'Web / PWA', how: 'На app.procoach.space выберите «Регистрация» и введите код в поле привязки.' },
  ]

  return (
    <Screen title="Мои каналы" back={back}>
      {channels.isPending ? (
        <Skeleton rows={3} />
      ) : channels.isError ? (
        <ErrorState title="Не удалось загрузить каналы" onRetry={() => void channels.refetch()} />
      ) : (
        <>
          <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
            {rows.map((r) => {
              const st = channels.data[r.key]
              return (
                <li key={r.key}>
                  <Row
                    title={r.title}
                    meta={st.linked ? `Подключён${st.account ? ` / ${st.account}` : ''}` : 'Не подключён'}
                  />
                </li>
              )
            })}
          </ul>

          {rows.some((r) => !channels.data[r.key].linked) && (
            <section className="pc-section" aria-labelledby="pc-link-title">
              <h2 id="pc-link-title" className="pc-section-title">
                Подключить ещё канал
              </h2>
              {code ? (
                <div className="pc-card">
                  <div className="pc-muted" style={{ fontSize: 14 }}>
                    Код действует {code.minutes} минут
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: 4, margin: '4px 0 8px' }} aria-live="polite">
                    {code.code}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                    {rows
                      .filter((r) => !channels.data[r.key].linked)
                      .map((r) => (
                        <li key={r.key} style={{ marginTop: 4 }}>
                          {r.title}: {r.how}
                        </li>
                      ))}
                  </ul>
                </div>
              ) : (
                <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={busy} onClick={() => void getCode()}>
                  {busy ? 'Получаю код…' : 'Получить код привязки'}
                </button>
              )}
              {codeError && (
                <div className="pc-alert" role="alert">
                  Код не получен. Попробуйте ещё раз.
                </div>
              )}
            </section>
          )}

          <div className="pc-note">
            На вопрос Штаб отвечает там, где его задали. Подключение канала не копирует туда переписку и не подписывает на
            рассылки. Отключить способ входа пока можно только через поддержку.
          </div>
        </>
      )}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// S17 Команда и доступы
// ---------------------------------------------------------------------------

/**
 * S17: приглашение создаётся только после просмотра условий и явного
 * подтверждения, а не при открытии формы. Просроченные - отдельно.
 */
export function TeamScreen() {
  const { api, toast } = useEnv()
  const navigate = useNavigate()
  const team = useTeam()
  const back = useBackTo('/club')
  const [preview, setPreview] = useState(false)
  const [days, setDays] = useState('7')
  const [uses, setUses] = useState('1')
  const [created, setCreated] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<null | 'limit' | 'other'>(null)
  const [revoking, setRevoking] = useState<{ id: string; code: string } | null>(null)
  const [showExpired, setShowExpired] = useState(false)

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.createInvite(Number(days), Number(uses))
      setPreview(false)
      setCreated(res.code)
      await team.refetch()
    } catch (e) {
      setError((e as { kind?: string })?.kind === 'limit' ? 'limit' : 'other')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (id: string) => {
    setBusy(true)
    try {
      await api.revokeInvite(id)
      setRevoking(null)
      await team.refetch()
      toast('Приглашение отозвано')
    } catch {
      toast('Не удалось отозвать. Попробуйте ещё раз.')
    } finally {
      setBusy(false)
    }
  }

  const link = (code: string) => `https://t.me/ProCoach_AI_bot?start=org_invite_${code}`

  if (team.isPending) {
    return (
      <Screen title="Команда и доступы" back={back}>
        <Skeleton rows={3} />
      </Screen>
    )
  }
  if (team.isError) {
    return (
      <Screen title="Команда и доступы" back={back}>
        <ErrorState title="Не удалось загрузить команду" onRetry={() => void team.refetch()} />
      </Screen>
    )
  }

  const active = team.data.invites.filter((i) => i.active)
  const expired = team.data.invites.filter((i) => !i.active)

  return (
    <Screen
      title="Команда и доступы"
      back={back}
      actionBar={
        team.data.canManage ? (
          <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => setPreview(true)}>
            Пригласить тренера
          </button>
        ) : undefined
      }
    >
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
        {team.data.members.map((m) => (
          <li key={m.userId}>
            <Row title={`${m.name}${m.isMe ? ' (вы)' : ''}`} meta={ROLE_LABEL[m.role]} />
          </li>
        ))}
      </ul>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        Тренер с полным доступом видит и меняет всё в клубе, кроме подписки, участников и удаления клуба. Доступ к
        отдельным группам пока не настраивается.
      </p>

      <section className="pc-section" aria-labelledby="pc-invites">
        <h2 id="pc-invites" className="pc-section-title">
          Приглашения
        </h2>
        {!team.data.canManage ? (
          <EmptyState title="Приглашать может владелец клуба" />
        ) : active.length === 0 ? (
          <EmptyState title="Активных приглашений нет" />
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            {active.map((i) => (
              <li key={i.id}>
                <div className="pc-list-row" style={{ cursor: 'default' }}>
                  <span className="pc-list-row__main">
                    <span className="pc-list-row__title" style={{ display: 'block' }}>
                      Код {i.code}
                    </span>
                    <span className="pc-list-row__meta">
                      До {dateLabel(i.expiresAt) ?? '?'}, осталось использований: {i.remainingUses}
                    </span>
                  </span>
                  <button type="button" className="pc-btn pc-btn--danger" onClick={() => setRevoking({ id: i.id, code: i.code })}>
                    Отозвать
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {expired.length > 0 && (
          <button type="button" className="pc-btn pc-btn--link" style={{ marginTop: 8 }} onClick={() => setShowExpired((v) => !v)}>
            {showExpired ? 'Скрыть историю приглашений' : `История приглашений (${expired.length})`}
          </button>
        )}
        {showExpired && (
          <ul className="pc-list" style={{ marginTop: 4 }}>
            {expired.map((i) => (
              <li key={i.id}>
                <Row title={`Код ${i.code}`} meta={i.expired ? `Истёк ${dateLabel(i.expiresAt) ?? ''}` : 'Использован'} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {preview && (
        <Sheet title="Пригласить тренера" onClose={() => setPreview(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            Приглашённый получит полный доступ к клубу: все направления и группы. Приглашение создаётся только после
            подтверждения ниже.
          </p>
          <div className="pc-form" style={{ marginTop: 0 }}>
            <Field label="Срок действия">
              {(p) => (
                <select {...p} className="pc-input" value={days} onChange={(e) => setDays(e.target.value)}>
                  <option value="1">1 день</option>
                  <option value="7">7 дней</option>
                  <option value="30">30 дней</option>
                </select>
              )}
            </Field>
            <Field label="Сколько человек может войти по нему">
              {(p) => (
                <select {...p} className="pc-input" value={uses} onChange={(e) => setUses(e.target.value)}>
                  <option value="1">1</option>
                  <option value="3">3</option>
                  <option value="10">10</option>
                </select>
              )}
            </Field>
          </div>
          {error === 'other' && (
            <div className="pc-alert" role="alert">
              Приглашение не создано. Попробуйте ещё раз.
            </div>
          )}
          {error === 'limit' && (
            <div className="pc-note" role="alert">
              На текущем тарифе в клубе больше нет мест для участников (или подписка клуба истекла). Пригласить тренера
              можно после смены тарифа.
              <button
                type="button"
                className="pc-btn pc-btn--block"
                style={{ marginTop: 8 }}
                onClick={() => navigate('/club/subscription')}
              >
                Открыть подписку клуба
              </button>
            </div>
          )}
          <button type="button" className="pc-btn pc-btn--block pc-btn--primary" style={{ marginTop: 16 }} disabled={busy} onClick={() => void create()}>
            {busy ? 'Создаю…' : 'Создать приглашение'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setPreview(false)}>
            Отмена
          </button>
        </Sheet>
      )}

      {created && (
        <Sheet title="Приглашение создано" onClose={() => setCreated(null)}>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Отправьте тренеру ссылку или код. Он увидит, в какой клуб и с какими правами вступает.
          </p>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 3 }}>{created}</div>
          <div className="pc-kv__value" style={{ margin: '8px 0 12px', wordBreak: 'break-all' }}>
            {link(created)}
          </div>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--primary"
            onClick={() => {
              void navigator.clipboard?.writeText(link(created)).then(
                () => toast('Ссылка скопирована'),
                () => toast('Не удалось скопировать, выделите ссылку вручную'),
              )
            }}
          >
            Скопировать ссылку
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setCreated(null)}>
            Готово
          </button>
        </Sheet>
      )}

      {revoking && (
        <Sheet title="Отозвать приглашение?" onClose={() => setRevoking(null)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            По коду {revoking.code} больше нельзя будет вступить в клуб. Уже вступившие останутся в команде.
          </p>
          <button type="button" className="pc-btn pc-btn--block pc-btn--danger" disabled={busy} onClick={() => void revoke(revoking.id)}>
            {busy ? 'Отзываю…' : 'Отозвать'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setRevoking(null)}>
            Отмена
          </button>
        </Sheet>
      )}
    </Screen>
  )
}

// S19 «Подписка клуба» - в SubscriptionScreens.tsx (тарифы, смена, оплата).
